import { useForkRef } from "@salt-ds/core";
import { useComponentCssInjection } from "@salt-ds/styles";
import { useWindow } from "@salt-ds/window";
import type { DataSource, SchemaColumn } from "@vuu-ui/vuu-data-types";
import { TableProvider } from "@vuu-ui/vuu-table-extras";
import {
  CustomHeader,
  DataRow,
  GroupToggleTarget,
  HeaderCellProps,
  RowActionHandler,
  RowProps,
  SelectionChangeHandler,
  ShowColumnHeaderMenus,
  TableConfig,
  TableConfigChangeHandler,
  TableRowClickHandler,
  TableRowSelectHandler,
  TableSelectionModel,
} from "@vuu-ui/vuu-table-types";
import type { DragDropState } from "@vuu-ui/vuu-ui-controls";
import {
  DragStartHandler,
  MeasuredContainer,
  MeasuredContainerProps,
  MeasuredSize,
  dragStrategy,
  reduceSizeHeight,
} from "@vuu-ui/vuu-ui-controls";
import { lowerCase, useId } from "@vuu-ui/vuu-utils";
import cx from "clsx";
import {
  CSSProperties,
  ComponentType,
  FC,
  ForwardedRef,
  RefObject,
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import { Row as DefaultRow } from "./Row";
import { TableCellBlock } from "./cell-block/cellblock-utils";
import { PaginationControl } from "./pagination";
import { getRowsInRenderOrder } from "./render-order";
import { ScrollLimitNotice } from "./scroll-limit-notice";
import { TableHeader } from "./table-header";
import { useMeasuredHeight } from "./useMeasuredHeight";
import { useTable } from "./useTable";
import { ScrollingAPI } from "./useTableScroll";

import tableCss from "./Table.css";

const classBase = "vuuTable";

export type TableNavigationStyle = "none" | "cell" | "row" | "tree";

// A dummy Table Row rendered once and not visible. We measure this to
// determine height of Row(s) and monitor it for size changes (in
// case of runtime density switch). This allows ListItem height to
// be controlled purely through CSS.
const RowProxy = forwardRef<
  HTMLDivElement,
  { className?: string; height?: number }
>(function RowProxy({ className = "vuuTableRow", height }, forwardedRef) {
  return (
    <div
      aria-hidden
      className={cx(className, `vuuRowProxy`)}
      ref={forwardedRef}
      style={{ height }}
    />
  );
});

export interface TableProps extends Omit<
  MeasuredContainerProps,
  "onDragStart" | "onDrop" | "onSelect" | "searchPattern"
> {
  /**
   * A react function component that will be rendered if there are no rows to display
   */
  EmptyDisplay?: ComponentType;
  /**
   * A custom implementation of HeaderCell which will be rendered in place of the
   * default HeaderCell. If provided, custom implementation is responsible for all
   * header behaviour (if required) - sorting, resizing etc.
   */
  HeaderCell?: FC<HeaderCellProps>;
  /**
   * A custom Row implementation that will be rendered in place of the default
   * Row.
   */
  Row?: FC<RowProps>;
  /**
   * Allow a block of cells to be selected. Typically to be copied.
   */
  allowCellBlockSelection?: boolean;
  /**
   * Allow columns to be manually resized by user. Default is true. This can also be
   * set on individual ColumnDescriptors for more fine grained control.
   */
  allowColumnResizing?: boolean;

  /**
   * If a ColumnPicker is offered for a table, default behaviour is to allow column
   * addition and removal. If a ColumnMenu is configured, default behaviour is likewise
   * to offer a 'remove' option. By setting this prop to false, column removal will not
   * be supported. No 'remove' menu item will be made available in the column menu and the
   * ColumnPicker will offer column position management only.
   */
  allowColumnRemoval?: boolean;
  /**
   * Allow column headers to be dragged to re-arrange
   */
  allowDragColumnHeader?: boolean;
  /**
   * Allow rows to be draggable
   */
  allowDragDrop?: boolean | dragStrategy;

  /**
   * Allow all rows be selected, use with checkbox coliumn.
   */
  allowSelectAll?: boolean;

  /**
   * If checkbox selection is in effect, can user select row by clicking row. If
   * false, checkbox must be clicked explicitly.
   */
  allowSelectCheckboxRow?: boolean;
  /**
   * if provided, first row of table will be selected on initial render.
   */
  autoSelectFirstRow?: true;
  /**
   * if provided, row with matching key will be selected. If not present, will silently fail.
   */
  autoSelectRowKey?: string;

  /**
   * Pixel height of header cells. If specified here, this will take precedence over CSS
   * values and Table will not respond to density changes.
   */
  colHeaderRowHeight?: number;

  /**
   * Provide configuration settings for Table. At minimun, column
   * descriptors must be provided.
   */
  config: TableConfig;
  dataSource: DataSource;

  disableFocus?: boolean;
  /**
   * Allows additional custom element(s) to be embedded immediately below column headers.
   * Could be used to present inline filters for example.
   * Accepts either a React Element or a Function Component or an array of these. If a React
   * Function Component is used, it will be passed the props described in BaseRowProps.
   */
  customHeader?: CustomHeader | CustomHeader[];
  /**
   * When rows are grouped, user can click group row(s) to expand/collapse display of child rows.
   * This allows precise configuration of where user may click to trigger toggle/collapse. When
   * row selection is also supported, clicking outside the region specified here will select or
   * deselect the row.
   * - toggle-icon - the small toggle icon must be clicked directly
   * - group-column (default) - user can click anywhere within the group column, i.e on the icon or the column text
   */
  groupToggleTarget?: GroupToggleTarget;

  /**
   * Defined how focus navigation within data cells will be handled by table.
   * Default is cell.
   */
  highlightedIndex?: number;

  /**
   * When provided, rows for which this returns false will not be selectable.
   * Those rows receive a `vuuTableRow-noSelect` CSS class and are skipped by
   * all selection logic (single, extended, checkbox and block selection).
   */
  isRowSelectable?: (dataRow: DataRow) => boolean;

  /**
   * Behaves in most respects like viewportRowLimit except that, when there are fewer
   * rows available than the limit set here, the Table height will reduce. This can be
   * useful where a Table is used in a dropdown control.
   */
  maxViewportRowLimit?: number;

  /**
   * Determines bahaviour of keyboard navigation , either row focused or cell focused.
   * `tree` is a specialised navigation behaviour only useful where table is being
   * used to present purely grouped data (see TreeTable)
   */
  navigationStyle?: TableNavigationStyle;
  /**
   * required if a fully featured column picker is to be available.
   * Available columns can be changed by the addition or removal of
   * one or more calculated columns.
   */
  onAvailableColumnsChange?: (columns: SchemaColumn[]) => void;
  /**
   * This callback will be invoked any time a config attribute of TableConfig
   * is changed. By persisting this value and providing it to the Table as a
   * prop, table state can be persisted across sessions.
   */
  onConfigChange?: TableConfigChangeHandler;

  onDragStart?: DragStartHandler;
  onDrop?: (dragDropState: DragDropState) => void;

  onHighlight?: (idx: number) => void;
  /**
   * callback invoked when user 'clicks' a table row. CLick triggered either
   * via mouse click or keyboard (default ENTER);
   */
  onRowClick?: TableRowClickHandler;
  onSelect?: TableRowSelectHandler;
  /**
   * Triggered when a block of cells is selected. CellBlock selection can be
   * effected with either mouse or keyboard.
   * - mouse: hold down mouse and drag over selection area
   * - keyboard: press and hold shift key from start cell, then use arrow keys
   *   to extend selection block.
   *
   * This callback is invoked when mouse is released or shift key released.
   */
  onSelectCellBlock?: (cellBlock: TableCellBlock) => void;

  onSelectionChange?: SelectionChangeHandler;
  renderBufferSize?: number;

  /**
   * Provide functionality for custom row actions. e.g. buttons embedded within row cells.
   * These will be available to any custom renderers via TableContext. e.g. a Row may
   * provide a 'Delete' or 'Cancel' button. Implement this functionality in a rowActionHandler.
   */
  rowActionHandlers?: Record<string, RowActionHandler>;

  /**
   * Allows opt-in to a predefined style pattern that renders a border around a row selection block.
   */
  rowSelectionBorder?: boolean;

  /**
   * Only applicable to grouped data. If there are selected rows which are not top-level
   * items and group items above are not already expanded, expand all group items in
   * the hierarchy above selected item. Selected items will thus always be visible, initially.
   * This affects items set at load time via autoSelectRowKey as well as items
   * selected programatically (ie not directly by user).
   * Nodes can of course be collapsed by user at runtime which may hide selected rows.
   * Note: this is not supported by all DataSource implementations
   */
  revealSelected?: boolean;
  /**
   * Pixel height of rows. If specified here, this will take precedence over CSS
   * values and Table will not respond to density changes.
   */
  rowHeight?: number;
  /**
   * imperative API for scrolling table
   */
  scrollingApiRef?: ForwardedRef<ScrollingAPI>;

  /**
   * If a search has been applied against data, this is the search text used.
   * Will be used to highlight matching text.
   */
  searchPattern?: string;
  /**
   * Selection behaviour for Table:
   * `none` selection disabled
   * `single` no more than one row may be selected
   * `single-no-deselect` no more than one row may be selected, one row is always selected, defaults to first visible
   * `extended` (default) multiple rows can be selected
   * `checkbox` same behaviour as extended, with checkbox column for selection
   */
  selectionModel?: TableSelectionModel;
  /**
   * if false, table rendered without headers. Useful when table is being included in a
   * composite component.
   */
  showColumnHeaders?: boolean;
  /**
   * if false, column headers will not display menu icon. If true, all available Column Menu
   * actions will be available via the menu. Alternatively, a map of specific column menu
   * permissions can be provided to allow control over which menu items are presented.
   */
  showColumnHeaderMenus?: ShowColumnHeaderMenus;
  /**
   * if true, pagination will be used to navigate data, scrollbars will not be rendered
   */
  showPaginationControls?: boolean;

  /**
   * As an alternative to sizing the Table height via CSS or via an explicit height value,
   * specify the number of rows to be displayed within the Viewport. The actual height
   * will then be the product of  viewportRowLimit and rowHeight. Row Height will be
   * determined in the usual way, it can be specified explicitly in a prop or set via
   * CSS. If both explicit height and viewportRowLimit are provided by props, rowHeight
   * will be derived from these. Do not pass props for all three values - height,
   * rowHeight and viewportRowLimit. That will be rejected.
   * Use maxViewportRowLimit rather than viewportRowLimit if the height of the table
   * should be reduced when fewer rows are actually available than the limit specified.
   */
  viewportRowLimit?: number;
}

const TableCore = ({
  EmptyDisplay,
  HeaderCell,
  Row = DefaultRow,
  allowCellBlockSelection,
  allowDragColumnHeader = true,
  allowDragDrop,
  allowSelectAll,
  allowSelectCheckboxRow,
  autoSelectFirstRow,
  autoSelectRowKey,
  // colHeaderRowHeight,
  config,
  containerRef,
  customHeader,
  dataSource,
  disableFocus = false,
  groupToggleTarget,
  highlightedIndex: highlightedIndexProp,
  isRowSelectable,
  id: idProp,
  lowerCaseSearchPattern,
  navigationStyle = "cell",
  // onAvailableColumnsChange,
  onConfigChange,
  onDragStart,
  onDrop,
  onHighlight,
  onRowClick: onRowClickProp,
  onSelect,
  onSelectCellBlock,
  onSelectionChange,
  renderBufferSize = 0,
  revealSelected,
  rowActionHandlers,
  rowHeight,
  scrollingApiRef,
  selectionModel = "extended",
  showColumnHeaders = true,
  showColumnHeaderMenus = true,
  showPaginationControls,
  size,
}: Omit<
  TableProps,
  | "colHeaderRowHeight"
  | "maxViewportRowLimit"
  | "resizeStrategy"
  | "rowHeight"
  | "rowSelectionStyle"
  | "searchPattern"
  | "viewportRowLimit"
> & {
  containerRef: RefObject<HTMLDivElement | null>;
  /**
   * We lowercase this once, on entry, which is the format in which it will be used.
   */
  lowerCaseSearchPattern: Lowercase<string>;
  rowHeight: number;
  size: MeasuredSize;
}) => {
  const id = useId(idProp);
  const {
    allRowsSelected,
    cellBlock,
    columns,
    dataRows,
    draggableRow,
    editSessionInProgress,
    focusCellPlaceholderKeyDown,
    focusCellPlaceholderRef,
    getRowOffset,
    handleColumnAction,
    headerState: { height: headerHeight, count: headerCount },
    headings,
    highlightedIndex,
    maxRangeEnd,
    onCheckBoxColumnHeaderClick,
    onHeaderHeightMeasured,
    onMoveColumn,
    onMoveGroupColumn,
    onRemoveGroupColumn,
    onResizeColumn,
    onRowClick,
    onSortColumn,
    onToggleGroup,
    rowClassNameGenerator,
    scrollLimitInEffect,
    scrollProps,
    tableAttributes,
    tableBodyRef,
    tableConfig,
    viewportMeasurements,
    ...tableProps
  } = useTable({
    allowCellBlockSelection,
    allowDragDrop,
    allowSelectCheckboxRow,
    autoSelectFirstRow,
    autoSelectRowKey,
    isRowSelectable,
    config,
    containerRef,
    dataSource,
    disableFocus,
    highlightedIndex: highlightedIndexProp,
    id,
    navigationStyle,
    onConfigChange,
    onDragStart,
    onDrop,
    onHighlight,
    onRowClick: onRowClickProp,
    onSelect,
    onSelectCellBlock,
    onSelectionChange,
    renderBufferSize,
    revealSelected,
    rowHeight,
    scrollingApiRef,
    selectionModel,
    showColumnHeaders,
    showPaginationControls,
    size,
  });

  const { selectionBookendWidth = 4 } = config;

  const contentContainerClassName = cx(`${classBase}-contentContainer`, {
    [`${classBase}-colLines`]: tableAttributes.columnSeparators,
    [`${classBase}-rowLines`]: tableAttributes.rowSeparators,
    [`${classBase}-zebra`]: tableAttributes.zebraStripes,
  });

  const cssScrollbarSize = {
    "--horizontal-scrollbar-height": `${viewportMeasurements.horizontalScrollbarHeight}px`,
    "--vertical-scrollbar-width": `${viewportMeasurements.verticalScrollbarWidth}px`,
  } as CSSProperties;

  const cssVariables = {
    ...cssScrollbarSize,
    "--content-height": `${viewportMeasurements.contentHeight}px`,
    "--content-width": `${viewportMeasurements.contentWidth}px`,
    "--pinned-width-left": `${viewportMeasurements.pinnedWidthLeft}px`,
    "--pinned-width-right": `${viewportMeasurements.pinnedWidthRight}px`,
    "--total-header-height": `${headerHeight}px`,
    "--viewport-body-height": `${viewportMeasurements.viewportBodyHeight}px`,
    "--table-selection-bookend-width": `${selectionBookendWidth}px`,
  } as CSSProperties;

  const headersReady = showColumnHeaders === false || headerHeight > 0;
  const readyToRenderTableBody = headersReady && dataRows.length > 0;

  return (
    <TableProvider
      dataSource={dataSource}
      menuActionHandler={handleColumnAction}
      rowActionHandlers={rowActionHandlers}
    >
      {showPaginationControls !== true ? (
        <div
          className={`${classBase}-scrollbarContainer`}
          ref={scrollProps.scrollbarContainerRef}
          style={cssVariables}
          tabIndex={-1}
        >
          <div className={`${classBase}-scrollbarContent`} />
        </div>
      ) : null}

      {dataSource.size === 0 && EmptyDisplay ? (
        <div className={`${classBase}-emptyDisplay`}>
          <EmptyDisplay />{" "}
        </div>
      ) : null}

      <div
        className={contentContainerClassName}
        ref={scrollProps.contentContainerRef}
        style={cssVariables}
      >
        <div
          {...tableProps}
          className={cx(`${classBase}-table`, {
            [`${classBase}-editing`]: editSessionInProgress,
          })}
          role="table"
          tabIndex={disableFocus ? undefined : -1}
        >
          {showColumnHeaders ? (
            <TableHeader
              HeaderCell={HeaderCell}
              allowDragColumnHeader={allowDragColumnHeader}
              allowSelectAll={
                allowSelectAll && selectionModel !== "checkbox-disabled"
              }
              allRowsSelected={allRowsSelected}
              columns={columns}
              customHeader={customHeader}
              headings={headings}
              onCheckBoxColumnHeaderClick={onCheckBoxColumnHeaderClick}
              onHeightMeasured={onHeaderHeightMeasured}
              onMoveColumn={onMoveColumn}
              onMoveGroupColumn={onMoveGroupColumn}
              onRemoveGroupColumn={onRemoveGroupColumn}
              onResizeColumn={onResizeColumn}
              onSortColumn={onSortColumn}
              showBookends={selectionBookendWidth > 0}
              showColumnHeaderMenus={showColumnHeaderMenus}
              tableConfig={tableConfig}
              tableId={id}
              virtualColSpan={scrollProps.virtualColSpan}
            />
          ) : null}
          {readyToRenderTableBody ? (
            <div
              className={cx(`${classBase}-body`, {
                [`${classBase}-scrollLimitInEffect`]: scrollLimitInEffect,
              })}
              ref={tableBodyRef}
            >
              {scrollLimitInEffect ? (
                <ScrollLimitNotice
                  maxScrollEnd={maxRangeEnd}
                  rowCount={dataSource.size}
                />
              ) : null}
              {getRowsInRenderOrder(dataRows).map(
                ({
                  dataRow,
                  nextDataRow,
                  precedesSelection,
                  previousDataRow,
                  selectionEnd,
                  selectionStart,
                }) => {
                  const ariaRowIndex = dataRow.index + headerCount + 1;
                  return (
                    <Row
                      aria-rowindex={ariaRowIndex}
                      // Row is memoised, passing the generated class name rather
                      // than neighbouring rows means a change to a neighbour only
                      // re-renders this row if its class name changes.
                      className={rowClassNameGenerator?.(
                        dataRow,
                        previousDataRow,
                        nextDataRow,
                      )}
                      columns={scrollProps.columnsWithinViewport}
                      // These are used for styling selection only. Rows are rendered
                      // in renderIndex order, not row order, so CSS sibling selectors
                      // cannot be used to identify selection block boundaries.
                      data-first-row={dataRow.index === 0 ? "true" : undefined}
                      data-precedes-selection={
                        precedesSelection ? "true" : undefined
                      }
                      data-selection-end={selectionEnd ? "true" : undefined}
                      data-selection-start={selectionStart ? "true" : undefined}
                      dataRow={dataRow}
                      groupToggleTarget={groupToggleTarget}
                      highlighted={highlightedIndex === ariaRowIndex}
                      isSelectable={isRowSelectable?.(dataRow)}
                      key={dataRow.renderIndex}
                      onClick={onRowClick}
                      offset={
                        showPaginationControls ? 0 : getRowOffset(dataRow)
                      }
                      onToggleGroup={onToggleGroup}
                      showBookends={selectionBookendWidth > 0}
                      searchPattern={lowerCaseSearchPattern}
                      virtualColSpan={scrollProps.virtualColSpan}
                      zebraStripes={tableAttributes.zebraStripes}
                    />
                  );
                },
              )}
              {/* 
                The focusCellPlaceholder allows us to deal with the situation where a cell 
                that has focus is scrolled out of the viewport. That cell, along with the 
                containing row, will be recycled to render another DataRow. The html table 
                cell must not retain focus once it is re-used but we need to track the 
                original focus, in case user uses arrow key to resume navigation. 
                The placeholder is fixed in place at the location where the focused cell 
                was last rendered. It assumes focus. If we get an arrowkey mousedown event 
                on the placeholder, the user has resumed navigation whilst the focused cell 
                is offscreen. We scroll back to the focus location and pass focus back to 
                the correct target cell.
              */}
              <div
                aria-hidden={true}
                className={`${classBase}-focusCellPlaceholder`}
                onKeyDown={focusCellPlaceholderKeyDown}
                ref={focusCellPlaceholderRef}
                tabIndex={-1}
              />
              {cellBlock}
            </div>
          ) : null}
        </div>
      </div>
      {/* 
        This keeps the heights of content container and scrollbar container aligned for
        cases where we rely on height: fit-content. (ScrollbarContainer isn't taken into 
        account because its absolutely positioned).
      */}
      <div
        className={`${classBase}-scrollbarFiller`}
        style={cssScrollbarSize}
      />
      {draggableRow}
    </TableProvider>
  );
};

export const Table = forwardRef(function Table(
  {
    EmptyDisplay,
    HeaderCell,
    Row,
    allowCellBlockSelection,
    allowDragColumnHeader,
    allowDragDrop,
    allowSelectAll,
    allowSelectCheckboxRow,
    autoSelectFirstRow,
    autoSelectRowKey,
    className: classNameProp,
    config,
    customHeader,
    dataSource,
    disableFocus,
    groupToggleTarget,
    colHeaderRowHeight: colHeaderRowHeightProp,
    height,
    highlightedIndex,
    isRowSelectable,
    id,
    maxViewportRowLimit,
    navigationStyle,
    onAvailableColumnsChange,
    onConfigChange,
    onDragStart,
    onDrop,
    onHighlight,
    onRowClick,
    onSelect,
    onSelectCellBlock,
    onSelectionChange,
    renderBufferSize,
    revealSelected,
    resizeStrategy,
    rowActionHandlers,
    rowHeight: rowHeightProp,
    rowSelectionBorder,
    scrollingApiRef,
    searchPattern = "",
    selectionModel,
    showColumnHeaders,
    showColumnHeaderMenus,
    showPaginationControls,
    style: styleProp,
    viewportRowLimit,
    width,
    ...htmlAttributes
  }: TableProps,
  forwardedRef: ForwardedRef<HTMLDivElement>,
) {
  const targetWindow = useWindow();
  useComponentCssInjection({
    testId: "vuu-table",
    css: tableCss,
    window: targetWindow,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const [size, _setSize] = useState<MeasuredSize>();

  const { measuredHeight: rowHeight, measuredRef: rowRef } = useMeasuredHeight({
    height: rowHeightProp,
  });

  const { measuredHeight: footerHeight, measuredRef: footerRef } =
    useMeasuredHeight({});

  const rowLimit = maxViewportRowLimit ?? viewportRowLimit;

  if (config === undefined) {
    throw Error(
      "vuu Table requires config prop. Minimum config is list of Column Descriptors",
    );
  }
  if (dataSource === undefined) {
    throw Error("vuu Table requires dataSource prop");
  }

  if (showPaginationControls && renderBufferSize !== undefined) {
    console.warn(
      `Table: When pagination controls are used, renderBufferSize is ignored`,
    );
  }

  if (rowLimit && height && rowHeightProp) {
    console.warn(
      `Table: when viewportRowLimit, rowHeight and height are used in combination, height is ignored`,
    );
    height = rowLimit * rowHeightProp;
  } else if (rowLimit && rowHeightProp) {
    height = rowLimit * rowHeightProp;
  } else if (rowLimit) {
    height = rowLimit * rowHeight;
  }

  const sizeRef = useRef<MeasuredSize>(undefined);
  const setSize = useCallback(
    (size: MeasuredSize) => {
      if (viewportRowLimit && !rowHeight) {
        sizeRef.current = size;
      } else if (
        size.height !== sizeRef.current?.height ||
        size.width !== sizeRef.current?.width
      ) {
        _setSize(size);
      }
    },
    [rowHeight, viewportRowLimit],
  );
  useMemo(() => {
    if (sizeRef.current && rowHeight) {
      const size = {
        ...sizeRef.current,
        height: rowHeight * (viewportRowLimit as number),
      };
      sizeRef.current = size;
      _setSize(size);
    }
  }, [rowHeight, viewportRowLimit]);

  // TODO render TableHeader here and measure before row construction begins
  // TODO we could have MeasuredContainer render a Provider and make size available via a context hook ?
  return (
    <MeasuredContainer
      {...htmlAttributes}
      className={cx(classBase, classNameProp, {
        [`${classBase}-pagination`]: showPaginationControls,
        [`${classBase}-rowSelection-bordered`]: rowSelectionBorder,
        [`${classBase}-maxViewportRowLimit`]: maxViewportRowLimit,
        [`${classBase}-viewportRowLimit`]: viewportRowLimit,
      })}
      height={height}
      id={id}
      onResize={setSize}
      ref={useForkRef(containerRef, forwardedRef)}
      resizeStrategy={resizeStrategy}
      style={
        {
          ...styleProp,
          "--col-header-row-height-prop":
            typeof colHeaderRowHeightProp === "number"
              ? `${colHeaderRowHeightProp}px`
              : undefined,
          "--row-height-prop":
            typeof rowHeightProp === "number"
              ? `${rowHeightProp}px`
              : undefined,
        } as CSSProperties
      }
      width={width}
    >
      <RowProxy ref={rowRef} height={rowHeightProp} />
      {size &&
      rowHeight &&
      (footerHeight || showPaginationControls !== true) ? (
        <TableCore
          EmptyDisplay={EmptyDisplay}
          HeaderCell={HeaderCell}
          Row={Row}
          allowCellBlockSelection={allowCellBlockSelection}
          allowDragColumnHeader={allowDragColumnHeader}
          allowDragDrop={allowDragDrop}
          allowSelectAll={allowSelectAll}
          allowSelectCheckboxRow={allowSelectCheckboxRow}
          autoSelectFirstRow={autoSelectFirstRow}
          autoSelectRowKey={autoSelectRowKey}
          config={config}
          containerRef={containerRef}
          customHeader={customHeader}
          dataSource={dataSource}
          disableFocus={disableFocus}
          groupToggleTarget={groupToggleTarget}
          highlightedIndex={highlightedIndex}
          id={id}
          navigationStyle={navigationStyle}
          onAvailableColumnsChange={onAvailableColumnsChange}
          onConfigChange={onConfigChange}
          onDragStart={onDragStart}
          onDrop={onDrop}
          onHighlight={onHighlight}
          onRowClick={onRowClick}
          onSelect={onSelect}
          onSelectCellBlock={onSelectCellBlock}
          onSelectionChange={onSelectionChange}
          renderBufferSize={
            showPaginationControls ? 0 : Math.max(5, renderBufferSize ?? 0)
          }
          revealSelected={revealSelected}
          rowActionHandlers={rowActionHandlers}
          isRowSelectable={isRowSelectable}
          rowHeight={rowHeight}
          scrollingApiRef={scrollingApiRef}
          lowerCaseSearchPattern={lowerCase(searchPattern)}
          selectionModel={selectionModel}
          showColumnHeaders={showColumnHeaders}
          showColumnHeaderMenus={showColumnHeaderMenus}
          showPaginationControls={showPaginationControls}
          size={reduceSizeHeight(size, footerHeight)}
        />
      ) : null}
      {showPaginationControls ? (
        <div className={`${classBase}-footer`} ref={footerRef}>
          <PaginationControl dataSource={dataSource} />
        </div>
      ) : null}
    </MeasuredContainer>
  );
});
